import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type SetupContext,
  watch,
} from 'vue';
import { parseEntrScanCode } from '@/app/scanCodeParser';
import { ScanInputPipeline } from '@/kiosk-core/scanner/scanInputPipeline';
import { addBreadcrumb, captureMessage } from '@/telemetry/sentry';
import type { ScanTicketScreenEvents } from './types';

const NO_INPUT_TIMEOUT_MS = 30_000;

export function useScanTicketController(
  emit: SetupContext<ScanTicketScreenEvents>['emit'],
  isBusy: () => boolean = () => false
) {
  const rawInput = ref('');
  const scanInputRef = ref<HTMLInputElement | null>(null);
  let focusInterval: number | null = null;
  let noInputTimer: number | null = null;
  let isMounted = false;
  let isProcessingInput = false;
  let receivedAnyInput = false;
  const pipeline = new ScanInputPipeline({
    isBusy,
    // Treat Enter as final only once the buffer parses as a real ticket, so a
    // wedge's Enter racing ahead of the last keystroke can't submit a partial.
    isComplete: (input) => parseEntrScanCode(input) !== null,
    onScan: (ticketCode) => {
      rawInput.value = '';
      emit('scan-ticket', ticketCode);
    },
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
    clearTimeout: (timer) => window.clearTimeout(timer),
    observer: {
      onDuplicate(input, elapsedMs) {
        addBreadcrumb('entr.scan', 'Duplicate scan blocked', {
          ticket_length: input.length,
          elapsed_ms: elapsedMs,
        });
      },
      onFinalize(input, inputKind) {
        addBreadcrumb('entr.scan', 'Scan input finalized', {
          ticket_length: input.length,
          input_kind: inputKind,
        });
      },
    },
  });

  // Clear the duplicate guard when a lookup finishes (busy → idle) so a visitor
  // who re-scans the same ticket right after a transient failure isn't deduped.
  // On a successful lookup the screen unmounts, so this only matters on failure.
  watch(isBusy, (busy, wasBusy) => {
    if (wasBusy && !busy) pipeline.resetDuplicateGuard();
  });

  function focusInput(): void {
    scanInputRef.value?.focus({ preventScroll: true });
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.preventDefault();
      focusInput();
      return;
    }

    pipeline.handleKeydown(event, rawInput.value);
  }

  function handleInput(): void {
    if (isProcessingInput) return;
    isProcessingInput = true;

    try {
      receivedAnyInput = true;
      if (!isMounted) return;
      pipeline.handleInput(rawInput.value);
    } finally {
      isProcessingInput = false;
    }
  }

  onMounted(() => {
    isMounted = true;
    void nextTick(focusInput);

    focusInterval = window.setInterval(() => {
      if (!isBusy() && document.activeElement !== scanInputRef.value) {
        focusInput();
      }
    }, 500);

    noInputTimer = window.setTimeout(() => {
      noInputTimer = null;
      if (receivedAnyInput || !isMounted) return;
      addBreadcrumb('entr.scan', 'No scanner input received', {
        timeout_ms: NO_INPUT_TIMEOUT_MS,
      });
      captureMessage('ENTR scan screen received no input for 30s', 'warning', {
        timeout_ms: NO_INPUT_TIMEOUT_MS,
      });
    }, NO_INPUT_TIMEOUT_MS);
  });

  onBeforeUnmount(() => {
    isMounted = false;
    pipeline.dispose();

    if (focusInterval !== null) {
      window.clearInterval(focusInterval);
      focusInterval = null;
    }

    if (noInputTimer !== null) {
      window.clearTimeout(noInputTimer);
      noInputTimer = null;
    }
  });

  return {
    rawInput,
    scanInputRef,
    back: () => emit('back'),
    focusInput,
    handleInput,
    handleKeydown,
    help: () => emit('help'),
  };
}
